/**
 * DroneMissionThread — the gov/requestor messaging surface for a drone mission (Wave-2 Phase 1
 * #346). Rendered in the SidePanel mission arm alongside the read-only DroneMissionDetailBody:
 * it resolves the assigned pilot's call-back phone (the single-mission detail GET) and renders
 * the reusable MissionThreadPanel (viewerRole 'requestor') with the gov COP message fns + the
 * "Call pilot" tel: link. A mission with no operator assigned yet has nobody to message → the
 * composer is disabled with an explanatory note.
 */
import { useEffect, useState, type JSX } from 'react';
import {
  fetchDroneMission,
  fetchMissionMessages,
  markMissionMessageRead,
  postMissionMessage,
  type DroneMissionView,
} from './api';
import { MissionThreadPanel } from './components/MissionThreadPanel';

export function DroneMissionThread({ mission }: { mission: DroneMissionView }): JSX.Element {
  // The list row carries operator_contact_phone only on the detail GET, so pull it here.
  const [phone, setPhone] = useState<string | null>(mission.operator_contact_phone ?? null);

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    void fetchDroneMission(mission.id, ac.signal)
      .then((m) => {
        if (alive && m) setPhone(m.operator_contact_phone ?? null);
      })
      .catch(() => {});
    return () => {
      alive = false;
      ac.abort();
    };
  }, [mission.id]);

  const hasOperator = mission.operator_agency_id != null;

  return (
    <div className="px-4 pb-4">
      <MissionThreadPanel
        missionId={mission.id}
        viewerRole="requestor"
        load={(signal) => fetchMissionMessages(mission.id, signal)}
        send={(content) => postMissionMessage(mission.id, content)}
        markRead={(msgId) => markMissionMessageRead(mission.id, msgId)}
        callPhone={hasOperator ? phone : null}
        disabled={!hasOperator}
        disabledNote="No operator assigned yet — message becomes available once a pilot accepts."
      />
    </div>
  );
}
